import * as React from "react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import Store from "logic/local-storage/Store";
import useDataLakeRetrievalMutation from "data-lake/hooks/useDataLakeRetrievalMutation";
import { Section, Spinner, IfPermitted } from "components/common";
import FilterRulesList, { StyledSectionComponent } from "components/streams/StreamDetails/output-filter/FilterRuleList";
import { Alert } from "components/bootstrap";
import SectionCountLabel from "components/streams/StreamDetails/SectionCountLabel";
import useStreamOutputFilters from "components/streams/hooks/useStreamOutputFilters";
import { DEFAULT_PAGINATION } from "stores/PaginationTypes";
import DataLakeSummary from "data-lake/components/DataLakeSummary";
import useStreamDataLake from "data-lake/hooks/useStreamDataLake";
import useStreamDataLakeConfig from "data-lake/hooks/useStreamDataLakeConfig";
import StreamDataLakeEnableButton from "data-lake/stream/StreamDataLakeEnableButton";
import DataLakeRetrieval from "data-lake/components/DataLakeRetrieval";
import SelectedInternalDataLakeProvider from "data-lake/contexts/SelectedInternalDataLakeProvider";
import DataLakeRetentionForm from "./DataLakeRetentionForm";
import { DATALAKE_SECTION_OPENED_KEY } from "../Constants";
export const Headline = styled.h2(
  ({ theme }) => css`
    margin-top: ${theme.spacings.sm};
    margin-bottom: ${theme.spacings.xs};
  `
);
const StreamDataLake = () => {
  var _a;
  const { streamId } = useParams();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [openDataLakeSection, setOpenDataLakeSection] = useState(Store.get(DATALAKE_SECTION_OPENED_KEY) === "true");
  const { data: config, isFetching } = useStreamDataLakeConfig(streamId);
  const {
    streamDataLakeData: { data: archive, isFetching: isFetchingArchive }
  } = useStreamDataLake(streamId);
  const { onDeleteDataLakeRetrievalOperationMutation } = useDataLakeRetrievalMutation();
  const { data: paginatedFilters, isSuccess: isLoadingFilterSuccess } = useStreamOutputFilters(
    streamId,
    "data-lake",
    pagination
  );
  useEffect(() => () => Store.set(DATALAKE_SECTION_OPENED_KEY, "false"), []);
  if (isFetching || isFetchingArchive) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const onPaginationChange = (newPage, newPerPage) => setPagination({
    ...pagination,
    page: newPage,
    perPage: newPerPage
  });
  const onDeleteRetrieveOperation = (retrievalOperationId) => onDeleteDataLakeRetrievalOperationMutation({ streamId, retrievalOperationId });
  const onSectionCollapse = (opened) => {
    Store.set(DATALAKE_SECTION_OPENED_KEY, opened ? "false" : "true");
    setOpenDataLakeSection(!opened);
  };
  const shouldShowDataLake = (config == null ? void 0 : config.enabled) && !!archive || (archive == null ? void 0 : archive.message_count) > 1 || ((_a = archive == null ? void 0 : archive.restore_history) == null ? void 0 : _a.length) > 0;
  return /* @__PURE__ */ React.createElement(
    Section,
    {
      title: "Data Lake",
      headerLeftSection: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["data_lake_config:update"] }, /* @__PURE__ */ React.createElement(StreamDataLakeEnableButton, { streamId, config })), shouldShowDataLake && /* @__PURE__ */ React.createElement(SectionCountLabel, null, "FILTERS ", isLoadingFilterSuccess ? paginatedFilters.pagination.total : 0)),
      actions: shouldShowDataLake && /* @__PURE__ */ React.createElement(DataLakeRetentionForm, { title: archive.stream_title, config }),
      collapsible: true,
      onCollapse: onSectionCollapse,
      defaultClosed: !openDataLakeSection,
      disableCollapseButton: !shouldShowDataLake
    },
    /* @__PURE__ */ React.createElement(SelectedInternalDataLakeProvider, null, shouldShowDataLake && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "default" }, "Storing messages in the Data Lake does not count towards license usage (although note that if they are also routed to the Search Cluster, they will still be counted)", /* @__PURE__ */ React.createElement("br", null), "and will be kept until the Data Lake Retention Policy is exceeded.", /* @__PURE__ */ React.createElement("br", null), "Note: newly laked logs can take up to an hour to be counted in these metrics."), /* @__PURE__ */ React.createElement(DataLakeSummary, { dataLake: archive }), paginatedFilters && /* @__PURE__ */ React.createElement(
      FilterRulesList,
      {
        streamId,
        paginatedFilters,
        onPaginationChange,
        destinationType: "data-lake",
        requiredPermissions: ["data_lake_config:update"]
      }
    ), /* @__PURE__ */ React.createElement(StyledSectionComponent, { title: "Retrieval Operations", collapsible: true, defaultClosed: true }, /* @__PURE__ */ React.createElement(DataLakeRetrieval, { archive, onDeleteOperation: onDeleteRetrieveOperation }))))
  );
};
export default StreamDataLake;
